export const BILLING_ROLE_KEYS = ["cashier", "waiter", "attendant"];
export const DEFAULT_BILLING_ROLE_DISCOUNT_CAP = 100;

const WAITER_BASELINE_ROLE_KEYS = new Set(["waiter", "attendant"]);

const roundCurrency = (value) => Number((Number(value) || 0).toFixed(2));

export const normalizeStaffRoleKey = (role = "") => {
  const normalized = String(role || "").trim().toLowerCase();
  return normalized === "attendent" ? "attendant" : normalized;
};

export const isBillingRole = (role = "") =>
  BILLING_ROLE_KEYS.includes(normalizeStaffRoleKey(role));

export const resolveStaffDefaultDiscountCap = (role = "") =>
  isBillingRole(role) ? DEFAULT_BILLING_ROLE_DISCOUNT_CAP : 0;

export const usesWaiterBaselinePermissions = (role = "") =>
  WAITER_BASELINE_ROLE_KEYS.has(normalizeStaffRoleKey(role));

// Mobile gives attendants the order-staff workflow plus a dashboard, while
// keeping aggregate sales/average-bill figures out of their overview.
export const canViewDashboardFinancialSummary = (role = "") =>
  normalizeStaffRoleKey(role) !== "attendant";

export const normalizeStaffDiscountPercent = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  const clamped = Math.max(0, Math.min(numeric, 100));
  return Math.round(clamped * 100) / 100;
};

export const formatDiscountPercent = (value) => {
  const normalized = normalizeStaffDiscountPercent(value);
  return Number.isInteger(normalized)
    ? String(normalized)
    : normalized.toFixed(2).replace(/\.?0+$/, "");
};

export const formatStaffCapForInput = (value) =>
  formatDiscountPercent(value || 0);

const findFirstNumeric = (values = []) => {
  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return numeric;
    }
  }
  return null;
};

export const resolveStaffDiscountCapFromProfile = (profile = {}) => {
  const role = normalizeStaffRoleKey(
    profile?.role ||
      profile?.staffrole ||
      profile?.staff_profile?.role ||
      profile?.data?.role ||
      profile?.data?.staffrole ||
      profile?.data?.staff_profile?.role
  );

  if (!isBillingRole(role)) {
    return null;
  }

  const rawCap = findFirstNumeric([
    profile?.staff_max_discount_percent,
    profile?.max_discount_percent,
    profile?.staff_profile?.max_discount_percent,
    profile?.data?.staff_max_discount_percent,
    profile?.data?.max_discount_percent,
    profile?.data?.staff_profile?.max_discount_percent,
  ]);

  return normalizeStaffDiscountPercent(
    rawCap ?? resolveStaffDefaultDiscountCap(role)
  );
};

export const resolveDiscountSelection = ({
  subtotal = 0,
  mode = "percent",
  percentValue = 0,
  amountValue = 0,
  capPercent = null,
}) => {
  const total = Math.max(0, Number(subtotal) || 0);
  const hasCap = capPercent !== null && capPercent !== undefined;
  const limitPercent = hasCap
    ? normalizeStaffDiscountPercent(capPercent)
    : 100;

  if (mode === "amount") {
    const requestedAmount = Math.max(0, Number(amountValue) || 0);
    const maxAllowedAmount =
      total > 0 ? ((hasCap ? limitPercent : 100) / 100) * total : 0;
    const amount =
      total > 0
        ? Math.min(requestedAmount, total, maxAllowedAmount)
        : 0;
    const percent =
      total > 0
        ? normalizeStaffDiscountPercent((amount / total) * 100)
        : 0;

    return {
      mode: amount > 0 ? "amount" : "percent",
      percent,
      amount: roundCurrency(amount),
      didClamp: requestedAmount - amount > 0.0001,
    };
  }

  const requestedPercent = Math.max(0, Number(percentValue) || 0);
  const percent = Math.min(requestedPercent, limitPercent, 100);

  return {
    mode: "percent",
    percent: normalizeStaffDiscountPercent(percent),
    amount: roundCurrency((total * percent) / 100),
    didClamp: requestedPercent - percent > 0.0001,
  };
};

export const isDiscountQuickPickDisabled = (percent, capPercent = null) => {
  if (capPercent === null || capPercent === undefined) {
    return false;
  }

  return Number(percent) > normalizeStaffDiscountPercent(capPercent) + 0.0001;
};

export const buildStaffCapPayload = (role = "", value = "") => {
  if (!isBillingRole(role)) {
    return 0;
  }

  if (value === null || value === undefined || value === "") {
    return resolveStaffDefaultDiscountCap(role);
  }

  return normalizeStaffDiscountPercent(value);
};

export const sanitizePercentInput = (value = "") => {
  const cleaned = String(value || "").replace(/[^0-9.]/g, "");
  const [whole = "", ...rest] = cleaned.split(".");
  const decimals = rest.join("").slice(0, 2);
  return decimals ? `${whole}.${decimals}` : whole;
};
